/**
 * 대화창 공용 도구: 숫자 전체 선택, 제목 끌기, 구간 채우기 창 (하리 테스트·메인 공용)
 */
import React, { useEffect, useRef, useState } from 'react';
import { SegRule, FillSpec, fillSegment, parseCommand, ruleToText, defaultRule, COMMAND_EXAMPLES, RestPos } from '../utils/segmentFill';

// 숫자 입력칸 포커스 시 전체 선택 (마우스업에 선택이 풀리지 않게 다음 틱에)
export const selectAllOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  const t = e.target;
  setTimeout(() => t.select(), 0);
};

// 팝업 제목 잡고 드래그 → 오프셋 (window 리스너라 빠르게 움직여도 놓치지 않음)
export function useDragOffset() {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const start = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  useEffect(() => {
    const move = (e: MouseEvent) => {
      const st = start.current;
      if (st) setOffset({ x: st.ox + e.clientX - st.mx, y: st.oy + e.clientY - st.my });
    };
    const up = () => { start.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);
  const onMouseDown = (e: React.MouseEvent) => {
    start.current = { mx: e.clientX, my: e.clientY, ox: offset.x, oy: offset.y };
    e.preventDefault();
  };
  return { offset, onMouseDown, reset: () => setOffset({ x: 0, y: 0 }) };
}

// ── 구간 채우기 다이얼로그: 한 줄 명령 ↔ 버튼이 같은 규칙을 조작, 나머지 자동 계산 미리보기 ──
export interface SegmentFillDialogProps {
  label: string;               // 예: "상단벽 · 구간 1/2 (2100mm)"
  total: number;
  orient: 'h' | 'v';
  initial: SegRule;
  onPreview: (rule: SegRule | null) => void; // 유효한 규칙만 도면 미리보기
  onApply: (rule: SegRule | null) => void;   // null = 규칙 해제(기본 분할)
  onCancel: () => void;
  spec: FillSpec;              // 재질 규격 (최소·최대·표준·반절·간격)
}
export const SegmentFillDialog: React.FC<SegmentFillDialogProps> = ({ label, total, orient, initial, onPreview, onApply, onCancel, spec }) => {
  const FILL_SPEC = spec;
  const [rule, setRule] = useState<SegRule>(initial);
  const [cmd, setCmd] = useState(ruleToText(initial, orient));
  const [msg, setMsg] = useState<string | null>(null);
  const drag = useDragOffset();
  const result = fillSegment(total, rule, FILL_SPEC);
  useEffect(() => { onPreview(result.error ? null : rule); }, [rule]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (r: SegRule) => { setRule(r); setCmd(ruleToText(r, orient)); setMsg(null); };
  const runCommand = () => {
    const pr = parseCommand(cmd, rule);
    if (pr.reset) { update(defaultRule(FILL_SPEC)); setMsg('기본 분할로 되돌림'); return; }
    if (pr.error || !pr.rule) { setMsg(pr.error ?? '해석 실패'); return; }
    update(pr.rule);
  };
  const [A, B] = orient === 'h' ? ['왼쪽', '오른쪽'] : ['위', '아래'];
  const REST: [RestPos, string][] = [['end', `${A}부터`], ['start', `${B}부터`], ['center', '나머지 가운데'], ['split', '나머지 양끝']];
  const btn = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '6px 4px', fontSize: 12, cursor: 'pointer', borderRadius: 6,
    border: active ? '2px solid #3182F6' : '1px solid #E5E8EB', background: active ? '#E8F3FF' : '#fff', fontWeight: active ? 'bold' : 'normal',
  });
  const sizes = result.sizes;
  const totalCheck = sizes.reduce((a, v) => a + v, 0) + FILL_SPEC.space * sizes.length;
  const label12: React.CSSProperties = { display: 'block', margin: '12px 0 4px', fontSize: 12, fontWeight: 'bold', color: '#555' };

  return (
    <div style={{ position: 'fixed', top: 90, right: 24, width: 400, zIndex: 1000, background: '#fff', borderRadius: 20, overflow: 'hidden',
      boxShadow: '0 12px 40px rgba(0,0,0,0.16)', transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)` }}>
      <div onMouseDown={drag.onMouseDown} style={{ background: '#fff', color: '#191F28', padding: '16px 20px 8px', borderRadius: 0,
        cursor: 'move', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>📐 구간 채우기 — {label}</span>
        <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ padding: 16 }}>
        <label style={{ ...label12, marginTop: 0 }}>명령 (Enter)</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={cmd} onChange={e => setCmd(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') runCommand(); }}
            onFocus={selectAllOnFocus} autoFocus placeholder={COMMAND_EXAMPLES[0]}
            style={{ flex: 1, padding: '6px 10px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8 }} />
          <button onClick={runCommand} style={{ padding: '6px 10px', fontSize: 12, border: '1px solid #3182F6', borderRadius: 8, background: '#fff', color: '#3182F6', cursor: 'pointer' }}>실행</button>
        </div>
        <div style={{ marginTop: 4, fontSize: 11, color: msg ? '#F04452' : '#999' }}>
          {msg ?? `예: ${COMMAND_EXAMPLES.slice(0, 4).join(' / ')}`}
        </div>

        <label style={label12}>채우는 방향 / 나머지 위치</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {REST.map(([v, t]) => <button key={v} onClick={() => update({ ...rule, rest: v })} style={btn(rule.rest === v)}>{t}</button>)}
        </div>

        <label style={label12}>기준 크기</label>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[FILL_SPEC.standard, FILL_SPEC.half].map(v => <button key={v} onClick={() => update({ ...rule, base: v })} style={btn(rule.base === v)}>{v}</button>)}
          <input type="number" value={rule.base} onFocus={selectAllOnFocus} onChange={e => update({ ...rule, base: Number(e.target.value) || 0 })}
            style={{ width: 80, padding: '5px 8px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8 }} />
        </div>

        {Object.keys(rule.locks).length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.entries(rule.locks).map(([i, v]) => (
              <span key={i} style={{ fontSize: 12, padding: '3px 8px', borderRadius: 12, background: '#fff3e0', border: '1px solid #ffb74d' }}>
                🔒 {i}번 {v}
                <button onClick={() => { const l = { ...rule.locks }; delete l[Number(i)]; update({ ...rule, locks: l }); }}
                  style={{ marginLeft: 4, border: 'none', background: 'none', cursor: 'pointer', color: '#e65100' }}>✕</button>
              </span>
            ))}
          </div>
        )}

        <label style={label12}>미리보기 ({orient === 'h' ? '왼쪽 → 오른쪽' : '위 → 아래'}) · 칸을 누르면 고정 크기 입력</label>
        <div style={{ display: 'flex', gap: 2, minHeight: 34 }}>
          {sizes.map((v, i) => (
            <div key={i} title={`${i + 1}번`}
              onClick={() => {
                const input = window.prompt(`${i + 1}번 패널 고정 크기 (mm)`, String(v));
                if (input && !isNaN(Number(input))) update({ ...rule, locks: { ...rule.locks, [i + 1]: Number(input) } });
              }}
              style={{ flex: v, minWidth: 28, padding: '4px 0', textAlign: 'center', fontSize: 11, cursor: 'pointer', borderRadius: 8,
                background: rule.locks[i + 1] ? '#ffe0b2' : v === rule.base ? '#e0e0e0' : '#c5cae9', border: '1px solid #9e9e9e' }}>
              <div style={{ fontSize: 9, color: '#777' }}>{i + 1}</div>{v}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: result.error ? '#F04452' : '#2e7d32', fontWeight: 'bold' }}>
          {result.error ? `⚠ ${result.error}` : `${sizes.join(' + ')} + 간격 ${FILL_SPEC.space}×${sizes.length} = ${totalCheck} ✓ (구간 ${total})`}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={() => onApply(null)} style={{ marginRight: 'auto', padding: '7px 12px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>기본 분할로</button>
          <button onClick={onCancel} style={{ padding: '7px 18px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>취소</button>
          <button onClick={() => onApply(rule)} disabled={!!result.error}
            style={{ padding: '7px 18px', fontSize: 13, border: 'none', borderRadius: 8, background: '#3182F6', color: '#fff', fontWeight: 'bold',
              cursor: result.error ? 'not-allowed' : 'pointer', opacity: result.error ? 0.4 : 1 }}>적용</button>
        </div>
      </div>
    </div>
  );
};

